import { Link } from 'react-router-dom';
import Chip from '@mui/material/Chip';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { PageHero } from '@/components/PageHero';
import { NotifyForm } from '@/components/NotifyForm';
import { Reveal } from '@/components/Reveal';
import { useSeo } from '@/seo/useSeo';

type Reality = { icon: SvgIconComponent; title: string; body: string };

const realities: Reality[] = [
  {
    icon: MedicalServicesOutlinedIcon,
    title: 'Downtime is a patient-care problem, not just a business one',
    body: 'A scheduling system or EHR outage doesn\'t just cost revenue — it can mean a clinic turning away or rescheduling patients who need care that day.',
  },
  {
    icon: HealthAndSafetyOutlinedIcon,
    title: 'HIPAA reaches the practice regardless of size',
    body: 'A two-chair dental practice and a large hospital system answer to the same Security Rule technical safeguards — there\'s no small-practice exemption for access control, audit logging, or encryption.',
  },
  {
    icon: GroupsOutlinedIcon,
    title: 'The IT contact is usually an office manager, not an IT department',
    body: 'Most small clinics and therapy practices have no in-house IT staff at all — the office manager or practice owner is the de facto point of contact for anything technology-related.',
  },
  {
    icon: AppsOutlinedIcon,
    title: 'The stack mixes general IT with practice-specific software',
    body: 'An EHR platform, imaging or lab-integration software, and insurance or billing systems sit alongside the same email and file-sharing tools any small business runs — not every MSP serving this vertical supports all of it well.',
  },
];

export function SolutionsHealthcare(): JSX.Element {
  useSeo({
    title: 'For MSPs serving healthcare practices',
    description:
      'HIPAA-aware compliance tracking, an encrypted credential vault for EHR and admin access, and patch-compliance evidence — built by an MSP that dogfoods Nexus on its own practice, not a healthcare edition bolted onto a generic platform.',
    path: '/solutions/healthcare',
  });

  return (
    <div>
      <PageHero
        eyebrow="For MSPs serving healthcare practices"
        title="Small clinics carry big-hospital compliance weight on a small-practice IT budget."
        subtitle="A dental office or therapy practice answers to the same HIPAA Security Rule as a hospital system, usually with no in-house IT staff at all. Nexus tracks that posture as part of the same platform we run our own practice on, not a healthcare-specific fork."
      >
        <Reveal delay={150}>
          <div className="mt-9 flex justify-center">
            <NotifyForm />
          </div>
        </Reveal>
      </PageHero>

      <section className="py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="mx-auto mb-14 max-w-2xl text-center">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-nexus-purple-light">
                What&apos;s actually different about a healthcare client
              </span>
              <h2 className="mt-3 font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                A small practice isn&apos;t a smaller version of a commercial client.
              </h2>
              <p className="mt-4 text-white/70">
                The regulatory weight is the same as a much larger organization&apos;s — the staff and
                budget to carry it usually aren&apos;t.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {realities.map((r, i) => {
              const Icon = r.icon;
              return (
                <Reveal key={r.title} delay={i * 90}>
                  <div className="h-full rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light">
                      <Icon fontSize="small" />
                    </div>
                    <h3 className="font-display text-[17px] font-semibold text-nexus-ink">{r.title}</h3>
                    <p className="mt-1.5 text-sm text-white/65">{r.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-y border-white/10 bg-white/[0.015] py-16 md:py-24">
        <div className="container-content max-w-[760px]">
          <Reveal>
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-nexus-purple-light">
              What that means inside Nexus
            </span>
            <h2 className="mt-3 font-display text-3xl font-bold text-nexus-ink md:text-[32px]">
              Not a healthcare edition — the same platform, used against the same weight of rules.
            </h2>
            <ul className="mt-6 flex flex-col gap-3">
              <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
                <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
                <span>
                  <Link to="/compliance/hipaa" className="text-nexus-purple-light no-underline hover:underline">
                    HIPAA posture tracking
                  </Link>{' '}
                  is one of the frameworks the compliance module tracks natively — not a request
                  we&apos;d have to build from scratch for a healthcare client.
                </span>
              </li>
              <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
                <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
                <span>
                  The{' '}
                  <Link to="/glossary/credential-vault" className="text-nexus-purple-light no-underline hover:underline">
                    credential vault
                  </Link>{' '}
                  gives EHR admin logins, billing-portal access, and imaging-system credentials the
                  same encrypted, rotation-tracked, access-logged handling as everything else — not
                  a spreadsheet a departing office manager takes the only copy of.
                </span>
              </li>
              <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
                <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
                <span>
                  <Link to="/glossary/patch-management" className="text-nexus-purple-light no-underline hover:underline">
                    Patch compliance
                  </Link>{' '}
                  evidence is a direct input to the HIPAA technical safeguards a practice has to
                  demonstrate, not a separate report assembled by hand before an audit.
                </span>
              </li>
              <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
                <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
                <span>
                  The{' '}
                  <Link to="/platform/client-staff-portal" className="text-nexus-purple-light no-underline hover:underline">
                    client &amp; staff portal
                  </Link>{' '}
                  gives a practice&apos;s office manager — usually the only point of contact, not a
                  dedicated IT lead — a simple way to submit and track a ticket.
                </span>
              </li>
              <li className="flex items-start gap-2.5 text-[15px] leading-relaxed text-white/75">
                <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-nexus-purple-light" />
                <span>
                  <Link to="/platform/compliance-qbr" className="text-nexus-purple-light no-underline hover:underline">
                    Compliance &amp; QBR reporting
                  </Link>{' '}
                  turns HIPAA posture into a summary a practice owner can actually read, instead of a
                  control list only a technician understands.
                </span>
              </li>
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container-content max-w-[760px] text-center">
          <Reveal>
            <Chip
              label="Honest about scope"
              size="small"
              className="mb-4 border border-nexus-purple/30 bg-nexus-purple/[0.14] font-semibold text-nexus-purple-light"
            />
            <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">
              We&apos;re not pitching a &quot;healthcare edition.&quot;
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-white/75">
              Nexus is one platform, not a vertical-specific fork — HIPAA is one framework among the
              several the compliance module tracks, alongside SOC 2, NIST CSF, PCI-DSS, and ISO
              27001. And to be direct about what the compliance module actually does: it tracks
              posture as implemented, partial, or planned per control, with evidence attached. It
              doesn&apos;t certify a practice as HIPAA compliant, and no platform honestly can — that
              determination is the practice&apos;s and its auditor&apos;s to make.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
              <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                Run your healthcare clients on a platform that tracks the posture they answer for.
              </h2>
              <p className="mx-auto mt-3 max-w-[520px] text-white/70">
                Join the design-partner cohort — we&apos;ll talk through what a small practice client
                actually needs, not a generic pitch.
              </p>
              <div className="mt-8 flex justify-center">
                <NotifyForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
